/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, Output, EventEmitter, Input, Inject, OnInit, ViewChild, HostListener, ElementRef, ChangeDetectorRef } from '@angular/core';
import { DxSelectBoxComponent } from 'devextreme-angular';
import { SysqueNumberBoxVM } from 'libs/sysque/shared/ui/src/lib/models/sysque-number-box-vm';
import { Observable } from 'rxjs';
import { GlobalConstants } from '../../../constants/global-constants';
import { CABLE_TRAY_SYSTEM_FACADE_TOKEN, ICableTraySystemFacade,} from '../../../../../../data-access/src/lib/domain/facades-models/cable-tray-system-facade-interface';
import { SysqueElectricalState } from '../../../models/sysque-electrical-state';
import { SelectedPanelDataItem } from '../../../models/selected-panel-data-item';

@Component({
  selector: 'sysque-cable-tray-system',
  templateUrl: './cable-tray-system.component.html',
  styleUrls: ['./cable-tray-system.component.scss'],
  standalone: false
})

export class CableTraySystemComponent implements OnInit {
  @Output() updateIsClearance = new EventEmitter<boolean>();
  @Output() onProjectChanged: EventEmitter<any> = new EventEmitter<any>();
  @Output() onWireFillChanged: EventEmitter<any> = new EventEmitter<any>();
  @Output() numberBoxItemsChanged: EventEmitter<any> = new EventEmitter<any>();
  @Output() projectFocusIn: EventEmitter<any> = new EventEmitter<string[]>();
  @Input() isClearance: any;
  @Input() needToShow = false;
  @Input() projectList: Observable<string[]>;
  @Input() wireSpecList: Observable<string[]>;
  @Input() resetEvent: EventEmitter<void>;
  numberBoxItems: SysqueNumberBoxVM;
  disableApplyBtn = true;
  selectedWireFill: string;
  selectedProject: string;
  dockedState = new SysqueElectricalState();
  isProjectOpened = false;
  isWireFillOpened = false;
  selectedItem: SelectedPanelDataItem;
  @ViewChild('wireFillSelectBoxRef', { static: false }) wireFillSelectBox: DxSelectBoxComponent;
  @ViewChild('projectSelectBoxRef', { static: false }) projectSelectBox: DxSelectBoxComponent;
  @ViewChild('projectSelectBoxRef', { read: ElementRef }) projectBoxContainer: ElementRef;
  @ViewChild('wireFillSelectBoxRef', { read: ElementRef }) wireFillBoxContainer: ElementRef;
  @ViewChild('leftInputValue') leftInput: ElementRef;
  @ViewChild('rightInputValue') rightInput: ElementRef;
  @ViewChild('topInputValue') topInput: ElementRef;
  @ViewChild('bottomInputValue') bottomInput: ElementRef;


  constructor(@Inject(CABLE_TRAY_SYSTEM_FACADE_TOKEN) public facade: ICableTraySystemFacade, private cd: ChangeDetectorRef,) { }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.projectSelectBox && this.projectSelectBox.opened) {
      this.resetProjectDropDown(event);
    }
    if (this.wireFillSelectBox && this.wireFillSelectBox.opened) {
      this.resetWireFillDropDown(event);
    }
  }

  resetWireFillDropDown(event: MouseEvent): void {
    const clickedInside = this.wireFillBoxContainer.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.wireFillSelectBox.opened = false;
      if (this.selectedWireFill) {
        this.disableApplyBtn = false;
        this.cd.detectChanges();
      }
    }
  }

  resetProjectDropDown(event: MouseEvent): void {
    const clickedInside = this.projectBoxContainer.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.projectSelectBox.opened = false;
        }
  }

  ngOnInit(): void {
    window.addEventListener('storage', this.storageEventListener.bind(this));
    this.selectedProject = '';
    this.selectedWireFill = '';
    this.disableApplyBtn = true;
    this.resetEvent?.subscribe(() => {
      if (this.leftInput) {
        this.leftInput.nativeElement.value = '';
      }
      if (this.rightInput) {
        this.rightInput.nativeElement.value = '';
      }
      if (this.topInput) {
        this.topInput.nativeElement.value = '';
      }
      if (this.bottomInput) {
        this.bottomInput.nativeElement.value = '';
      }
      this.cd.markForCheck(); // Mark for change detection
    });
  }

  storageEventListener(event: StorageEvent): void {
    if (!event) return;
    if (event.key === GlobalConstants.ELEMENT_SELECTION_STATUS) {
      this.enableApplyBtn(JSON.parse(event.newValue));
    } else if (event.key === GlobalConstants.SELECTED_SYSTEM_NAME) {
      this.selectedWireFill = '';
      this.disableApplyBtn = true;
      this.cd.detectChanges();
      this.selectedProject = '';
      this.resetDropDown(this.projectSelectBox);
      this.resetDropDown(this.wireFillSelectBox);
    }
  }

  enableApplyBtn(elementSelected: boolean): void {    
    if (elementSelected && this.selectedProject && this.selectedWireFill) {
      this.disableApplyBtn = false;
    } else {
      this.disableApplyBtn = true;
    }
    this.cd.detectChanges();
  }

  onCableTrayValueChanged(event: any): void {
    this.updateIsClearance.emit(event.detail);
  }

  onFocusOutEvent(event: any, textLabel: string): void {
    this.numberBoxItems = { label: textLabel, value: event.target.value, isMetric: false };
    this.numberBoxItemsChanged.emit(this.numberBoxItems);
  }

  public onFocusIn(): void {
    this.projectFocusIn.emit();
  }

  onProjectValueChanged(event: any): void {
    if (event.value) {
      this.selectedProject = event.value;
      this.selectedWireFill = '';
      this.disableApplyBtn = true;
      this.cd.detectChanges();
      this.resetDropDown(this.wireFillSelectBox);
      this.onProjectChanged.emit(event.value);
    }
  }

  onWireFillSpecValueChanged(event: any): void {
    if (event.value) {
      this.selectedWireFill = event.value;
      const selectionExists: boolean = this.facade.getLocalStorage(GlobalConstants.ELEMENT_SELECTION_STATUS);
      this.disableApplyBtn = !selectionExists;
      this.cd.detectChanges();
      this.onWireFillChanged.emit(this.selectedWireFill);
    }
  }

  resetDropDown(selectBox: DxSelectBoxComponent): void {
    if (selectBox.instance.option('isDirty')) {
      selectBox.instance.option('isDirty', false);
      if (!this.selectedWireFill) {
        selectBox.instance.reset();
        this.disableApplyBtn = true;
        this.cd.detectChanges();
      }      
      selectBox.instance.close();
    }
    if (this.isWireFillOpened || this.isProjectOpened) {
      selectBox.instance.close();
    }
  }

  onProjectOpened(): void {
    this.isProjectOpened = true;   
    this.projectSelectBox.instance.option('isDirty', true);
    this.resetDropDown(this.wireFillSelectBox);
  }

  onWireFillSpecOpened(): void {
    this.wireFillSelectBox.instance.option('isDirty', true);
    this.isWireFillOpened = true;
  }

  applySelection(): void {
    this.dockedState = this.facade.getLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);
    this.selectedItem = {
      selectedProjectName: this.dockedState.projectName,
      selectedPanelName: '',
      selectedCircuitLists: [],
      selectedSystemName: this.dockedState.selectedSystem,
      selectedCategory: this.dockedState.selectedCategory,
      selectedWireFillSpec: this.dockedState.wirefillSpec,
    };
    this.facade.applyPanelDataLogic(this.selectedItem, true);
  }
}
